/**
 * RolloverSourcesSection
 * T3-032 — many-to-1 rolled funds (single-investor path). Lets a new contract be linked
 * to MULTIPLE prior contracts (same investor), each carrying its own rolled amount. A
 * single source is just one row (looks/behaves like the old single picker).
 *
 * Owns an array of source rows [{ source_contract_id, rolled_amount, reference,
 * user_reference }]. Each row reuses PriorContractAutocomplete + a per-source amount
 * field, caps the amount against that source's remaining (computeRolledExceed), and the
 * section as a whole checks that the rows sum to the contract's Rolled Funds total.
 *
 * Reports two things up via onChange(sources, errorMessageOrNull):
 *   - sources: the normalised [{ source_contract_id, rolled_amount }] list for the payload
 *   - error: a blocking message (or null) so the wizard can gate Next (rolledFundsError)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import PriorContractAutocomplete, { computeRolledExceed, sliceOptionForInvestor } from './PriorContractAutocomplete';
import CurrencyInput from '../common/CurrencyInput';

const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
const fmt2 = (n) => round2(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

function RolloverSourcesSection({
  sources = [],
  onRowsChange,     // (rows[]) => void — parent owns the rows array (persists across steps)
  onChange,         // (sourcesForPayload[], errorOrNull) => void — for payload + Next-gate
  rolledFundsTotal = 0,
  excludeId = null,
  currencyCode = null,
  depositDate = null,
  investorId = null,
  investorName = null,
  disabled = false,
}) {
  // Live FX rate per row (source ccy -> contract ccy), captured from each autocomplete.
  // Keyed by row index; used by the cap check for cross-currency sources.
  const fxByRow = useRef({});

  // "Add source" reveals ONE transient blank row to fill (not persisted until it gets a
  // contract). This keeps blank/unlinked rows from lingering below the real sources.
  const [showBlank, setShowBlank] = useState(false);

  // Display rows = the real sources (a source is "real" once it has a contract picked),
  // plus at most one blank editable row: always when there are no real sources yet, or
  // transiently when the user clicked "Add source". No dangling blank block otherwise.
  const rows = useMemo(() => {
    const real = (sources || []).filter((r) => r.source_contract_id);
    const blank = { source_contract_id: null, rolled_amount: '', reference: null, user_reference: null, _key: 'rs_blank' };
    if (real.length === 0) return [blank];
    return showBlank ? [...real, blank] : real;
  }, [sources, showBlank]);

  const totalTarget = round2(rolledFundsTotal);

  // Per-row cap + section-sum validation. Pure derivation from props.
  // `rowPending` marks rows that CANNOT be validated yet because the source's remaining
  // balance hasn't resolved — distinct from a hard error. A pending row still BLOCKS Next
  // (we must not let an over-cap amount slip past the client while balances load, deferring
  // to a late server 400), but it renders as a soft "checking…" hint, not a red error.
  const { rowErrors, rowInfos, sectionError, sourcesForPayload, sum } = useMemo(() => {
    const rowInf = [];
    const rowPend = [];
    const rowErrs = rows.map((r, i) => {
      const amt = parseFloat(r.rolled_amount) || 0;
      rowInf[i] = null;
      rowPend[i] = false;
      if (!r.source_contract_id) return amt > 0 ? 'Pick a prior contract' : null;
      if (amt <= 0) return 'Enter an amount';
      if (!r._option) {
        // Balances not resolved yet (the /rollable-list fetch hasn't returned, or the row
        // holds a minimal rehydrate stub). Mark PENDING so the section blocks Next until the
        // cap can actually be checked — instead of the old silent pass that let an over-cap
        // amount through to a server 400 at Generate Schedule.
        rowPend[i] = true;
        rowInf[i] = 'Checking available balance…';
        return null;
      }
      // T3-033 SLICE-ONLY: cap against THIS investor's slice of a multi-investor source (never
      // the whole pool). noSlice = the source has a per-investor breakdown but none for this
      // investor → block (never-cross-investor).
      const { option: sliceOpt, noSlice } = sliceOptionForInvestor(r._option, investorId);
      if (noSlice) return `This investor has no rollable balance on ${r._option.reference || 'this source'}`;
      const { wouldExceed, remainingConverted, noFx } = computeRolledExceed({
        selectedOption: sliceOpt,
        rolledFundsAmount: amt,
        currencyCode,
        fxRate: fxByRow.current[r._key],
      });
      if (noFx) return `No FX rate for ${r._option.currency_code}/${currencyCode}`;
      if (wouldExceed) return `Exceeds remaining (${currencyCode} ${fmt2(remainingConverted || 0)})`;
      // No error: the full-width info line (source remaining after THIS row's allocation,
      // out of the source's total remaining). Mirrors the picker's old helper wording.
      if (remainingConverted !== null) {
        rowInf[i] = `Remaining after allocation: ${currencyCode || ''} ${fmt2(remainingConverted - amt)} of ${fmt2(remainingConverted)}`;
      }
      return null;
    });

    // Duplicate-source guard.
    const seen = new Map();
    rows.forEach((r, i) => {
      if (!r.source_contract_id) return;
      if (seen.has(r.source_contract_id) && !rowErrs[i]) rowErrs[i] = 'Duplicate source';
      seen.set(r.source_contract_id, i);
    });

    const s = round2(rows.reduce((acc, r) => acc + (parseFloat(r.rolled_amount) || 0), 0));
    const payload = rows
      .filter((r) => r.source_contract_id && (parseFloat(r.rolled_amount) || 0) > 0)
      .map((r) => ({ source_contract_id: r.source_contract_id, rolled_amount: round2(r.rolled_amount) }));

    let sErr = null;
    if (totalTarget > 0) {
      if (rowErrs.some(Boolean)) {
        // Surface the ACTUAL row problem(s) (e.g. "Exceeds remaining …") rather than a
        // generic "fix the highlighted rows", so the floating toast tells the user what's
        // wrong. Tag each by its source reference when we have one, and de-duplicate.
        const detailed = rows
          .map((r, i) => rowErrs[i]
            ? `${r.reference || r._option?.reference || `Source ${i + 1}`}: ${rowErrs[i]}`
            : null)
          .filter(Boolean);
        const unique = Array.from(new Set(detailed));
        sErr = unique.length ? unique.join('; ') : 'Fix the highlighted source rows before continuing';
      } else if (rowPend.some(Boolean)) {
        // A picked source's balance is still loading — block Next (so the cap is actually
        // checked before advancing) with a soft "please wait" message rather than a hard error.
        sErr = 'Checking the available balance on the selected prior contract(s) — one moment.';
      } else if (Math.abs(s - totalTarget) > 0.005) {
        const diff = round2(Math.abs(totalTarget - s));
        sErr = `Rolled Funds (${currencyCode || ''} ${fmt2(totalTarget)}) doesn't match the sources total `
          + `(${currencyCode || ''} ${fmt2(s)}) — off by ${currencyCode || ''} ${fmt2(diff)}. `
          + `Adjust the Rolled Funds amount or the source rows so they match.`;
      } else if (payload.length === 0) {
        sErr = 'Add at least one prior contract for the rolled funds';
      }
    }
    return { rowErrors: rowErrs, rowInfos: rowInf, sectionError: sErr, sourcesForPayload: payload, sum: s };
  }, [rows, totalTarget, currencyCode, investorId]);

  // Report sources + blocking error up whenever they change (value-gated in the parent).
  const lastReport = useRef('');
  useEffect(() => {
    const sig = JSON.stringify(sourcesForPayload) + '|' + (sectionError || '');
    if (sig === lastReport.current) return;
    lastReport.current = sig;
    if (onChange) onChange(sourcesForPayload, sectionError);
  }, [sourcesForPayload, sectionError, onChange]);

  // Stable per-row keys (monotonic counter) so the per-row FX map + React keys survive
  // edits/reorders without depending on Date.now()/index.
  const keySeq = useRef(0);
  const nextKey = () => `rs_${keySeq.current++}`;

  // Persist to the parent form ONLY the real rows (a source picked). The transient blank
  // is a display-only affordance and never gets saved as an empty source.
  const commit = (next) => {
    const real = next.filter((r) => r.source_contract_id).map((r) => ({ ...r, _key: r._key || nextKey() }));
    if (onRowsChange) onRowsChange(real);
  };

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    // If the (previously blank) row just got a source, it's now real — drop the transient blank.
    if (patch.source_contract_id) setShowBlank(false);
    commit(next);
  };
  // Reveal one blank editable row to fill; it isn't persisted until a source is picked.
  const addRow = () => setShowBlank(true);
  const removeRow = (idx) => {
    const target = rows[idx];
    // Removing the transient blank just hides it — nothing to persist.
    if (target && !target.source_contract_id) { setShowBlank(false); return; }
    commit(rows.filter((_, i) => i !== idx));
  };

  // Compact: each source is ONE line — picker (grows) + amount (fixed) + remove — side by
  // side. Multiple sources stack as separate lines with a faint divider. No card chrome.
  const showRemove = rows.length > 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((row, idx) => (
        <Box
          key={row._key || idx}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 0.25,
            ...(idx > 0 ? { pt: 1 } : {}),
          }}
        >
          {/* 50/50 side-by-side row (unchanged): picker | amount | remove */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PriorContractAutocomplete
                value={row.source_contract_id || null}
                onChange={(id, obj) => {
                  const patch = {
                    source_contract_id: id,
                    reference: obj?.reference || null,
                    user_reference: obj?.user_reference || null,
                    _option: obj || null,
                  };
                  // When the source was just created/attached via the "Enter Historical
                  // Contract" dialog, it carries the capital amount the user captured there.
                  // Seed this row's amount with it so the user isn't asked to re-enter the
                  // same figure — only when the row's amount is still blank, so we never
                  // clobber a value the user already typed.
                  const captured = parseFloat(obj?.capturedCapitalAmount);
                  if (Number.isFinite(captured) && captured > 0 && !(parseFloat(row.rolled_amount) > 0)) {
                    patch.rolled_amount = round2(captured);
                  }
                  updateRow(idx, patch);
                }}
                onFxRate={(rate, meta) => {
                  fxByRow.current[row._key] = rate;
                  // Adopt the picker's reported option whenever it is RICHER than what we hold.
                  // On edit-rehydrate the picker first reports a minimal STUB (id + refs, NO
                  // remaining_balance) before its /rollable-list fetch resolves; the old one-shot
                  // `if (!row._option)` guard latched that stub and never upgraded to the fetched
                  // option (with remaining_balance), so computeRolledExceed read remaining=0 and
                  // showed a false "Exceeds remaining (0.00)". Prefer an option that carries a real
                  // balance over one that doesn't (value-gated so it can't churn).
                  const next = meta && meta.selectedOption;
                  if (!next) return;
                  const cur = row._option;
                  const nextHasBalance = next.remaining_balance != null || Array.isArray(next.per_investor_remaining);
                  const curHasBalance = cur && (cur.remaining_balance != null || Array.isArray(cur.per_investor_remaining));
                  if (!cur || (nextHasBalance && !curHasBalance)) {
                    updateRow(idx, { _option: next });
                  }
                }}
                excludeId={excludeId}
                excludeIds={rows.filter((_, i) => i !== idx).map((r) => r.source_contract_id).filter(Boolean)}
                disabled={disabled}
                rolledFundsAmount={row.rolled_amount}
                currencyCode={currencyCode}
                depositDate={depositDate}
                investorId={investorId}
                investorName={investorName}
                fallbackReference={row.reference}
                fallbackUserReference={row.user_reference}
                // The section renders ONE full-width line beneath the row (info or error);
                // suppress the picker's own cramped half-width helper. Still flag red on error.
                suppressHelperText
                error={rowErrors[idx] ? { message: '' } : undefined}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CurrencyInput
                label={`Amount${currencyCode ? ` (${currencyCode})` : ''}`}
                value={row.rolled_amount}
                onChange={(val) => updateRow(idx, { rolled_amount: val })}
                // Amount is greyed out until a prior contract is picked for this row
                // (pick the source first — enforces order + avoids a lost-amount quirk).
                disabled={disabled || !row.source_contract_id}
                error={!!rowErrors[idx]}
              />
            </Box>
            {showRemove && (
              <IconButton
                aria-label="Remove source"
                onClick={() => removeRow(idx)}
                disabled={disabled}
                sx={{ mt: 0.5 }}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {/* One full-width line beneath the row (spans both fields). Error takes precedence;
              otherwise the "Remaining after allocation" info for the selected source. */}
          {(rowErrors[idx] || rowInfos[idx]) && (
            <Typography variant="caption" color={rowErrors[idx] ? 'error' : 'textSecondary'}>
              {rowErrors[idx] || rowInfos[idx]}
            </Typography>
          )}
        </Box>
      ))}

      <Button startIcon={<AddIcon />} onClick={addRow} disabled={disabled} size="small" sx={{ alignSelf: 'flex-start' }}>
        Add source
      </Button>
      {totalTarget > 0 && (
        <Typography
          variant="caption"
          color={sectionError ? 'error' : 'textSecondary'}
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {sectionError || `Allocated ${currencyCode || ''} ${fmt2(sum)} / ${fmt2(totalTarget)}`}
        </Typography>
      )}
    </Box>
  );
}

export default RolloverSourcesSection;
